import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';

import { Tooltip } from '../atoms/Tooltip.jsx';

export const TakeOverRequestDialog = ({
  onClose,
  onSubmit,
  isSubmitting,
  hasBeenReleasedByTimeout,
  hasDataFileBeenFinished,
}: {
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  hasBeenReleasedByTimeout: boolean;
  hasDataFileBeenFinished: boolean;
}) => {
  const { t } = useTranslation('@myn/import-hub');

  const message = hasBeenReleasedByTimeout
    ? t(
        'Another user requested to take control of this wizard, but you did not respond in time. Editing control has been transferred. You can request control back or leave the page.',
      )
    : t(
        'This wizard is currently in use by another user. You can request control to start editing or leave the page.',
      );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle className="flex flex-row items-center justify-center gap-2">
              <WarningAltFilled className="size-5 fill-warning-main" />
              <p>{t('Do you want to edit this wizard?')}</p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent>
            <DialogDescription>{message}</DialogDescription>
          </DialogContent>
          <DialogFooter>
            <Button
              data-dd-action-name="Take over request: cancel and leave"
              size="large"
              variant="text"
              onClick={onClose}
            >
              {t('Cancel and leave')}
            </Button>
            <Tooltip
              description={t(
                'This action is no longer available because the wizard has already been finished',
              )}
              side="top"
              align="end"
              disabled={!hasDataFileBeenFinished}
            >
              <div>
                <Button
                  data-dd-action-name="Take over request: take over"
                  onClick={onSubmit}
                  size="large"
                  color="warning"
                  disabled={isSubmitting || hasDataFileBeenFinished}
                >
                  {isSubmitting && (
                    <ProgressCircular size="xsmall" className="size-4" />
                  )}
                  {t('Take control')}
                </Button>
              </div>
            </Tooltip>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
